import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

export default function CreatePost() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, locationData } = useContext(AuthContext);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    server_id: location.state?.serverId || '',
    title: '',
    description: '',
    tags: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    const token = localStorage.getItem('token');
    axios.get(`${API}/api/servers`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true
    })
      .then(response => {
        setServers(response.data);
      })
      .catch(error => console.error('Error fetching servers:', error));
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const postData = {
        server_id: formData.server_id,
        title: formData.title,
        description: formData.description,
        location: locationData || { country: 'Unknown', state: 'Unknown', town: 'Unknown' },
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t)
      };

      await axios.post(`${API}/api/posts`, postData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });

      toast.success('Post created successfully!');
      navigate(`/server/${formData.server_id}`);
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <nav className="border-b-4 border-black bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-20">
            <Button
              data-testid="back-button"
              onClick={() => navigate(-1)}
              variant="ghost"
              className="mr-4 border-2 border-black rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl md:text-3xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>Add a Moment</h1>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white border-4 border-black rounded-3xl p-8 md:p-10 shadow-brutal-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="server" className="text-sm font-bold text-slate-900">Server *</Label>
              <Select
                value={formData.server_id}
                onValueChange={(value) => setFormData({...formData, server_id: value})}
              >
                <SelectTrigger data-testid="server-select" id="server" className="mt-1 h-14 border-2 border-black rounded-xl font-semibold">
                  <SelectValue placeholder="Select a server" />
                </SelectTrigger>
                <SelectContent>
                  {servers.map(server => (
                    <SelectItem key={server.server_id} value={server.server_id}>{server.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="title" className="text-sm font-bold text-slate-900">Title *</Label>
              <Input
                id="title"
                data-testid="title-input"
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., Looking for tennis partner in Brooklyn"
                className="mt-1 h-14 border-2 border-black rounded-xl font-medium"
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-sm font-bold text-slate-900">Description *</Label>
              <Textarea
                id="description"
                data-testid="description-input"
                required
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe what you're looking for..."
                rows={6}
                className="mt-1 border-2 border-black rounded-xl font-medium resize-none"
              />
            </div>

            <div>
              <Label htmlFor="tags" className="text-sm font-bold text-slate-900">Tags (optional)</Label>
              <Input
                id="tags"
                data-testid="tags-input"
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({...formData, tags: e.target.value})}
                placeholder="e.g., beginner, weekends, mornings (comma separated)"
                className="mt-1 h-14 border-2 border-black rounded-xl font-medium"
              />
              <p className="text-xs text-slate-500 mt-1">Separate tags with commas</p>
            </div>

            {locationData && (
              <div className="bg-lime-100 border-2 border-black rounded-xl p-4">
                <p className="text-sm font-bold text-slate-900">Location</p>
                <p className="text-sm text-slate-700">{locationData.town}, {locationData.state}, {locationData.country}</p>
              </div>
            )}

            <Button
              data-testid="submit-button"
              type="submit"
              disabled={loading || !formData.server_id}
              className="w-full h-14 text-lg font-bold bg-indigo-500 hover:bg-indigo-600 text-white rounded-full shadow-brutal border-2 border-black"
            >
              {loading ? 'Adding...' : 'Add Moment'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}